import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  FaEdit,
  FaDownload,
  FaCog,
  FaTrash,
  FaChevronDown,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import { getData, putData, deleteData } from "../../services/FetchNodeservices";

/* ================================
   HELPERS
================================ */
const toCamelCase = (str) =>
  str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/* ================================
   MODAL
================================ */
function Modal({ open, title, onClose, children }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl bg-white rounded-2xl shadow-2xl">
        <div className="p-5 border-b flex justify-between items-center">
          <h3 className="text-xl font-semibold">{title}</h3>
          <button className="text-xl" onClick={onClose}>✕</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

/* ================================
   MAIN COMPONENT
================================ */
export default function CandidateReport() {
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [selected, setSelected] = useState(null);

  /* ---------- COLUMN VISIBILITY ---------- */
const [columns, setColumns] = useState({
  position: true,
  company_applied: true,
  candidate_name: true,
  email_id: true,
  phone_number: true,
  qualification: true,
  current_company: true,
  current_designation: true,
  location: true,
  current_ctc: true,
  expected_ctc: true,
  notice_period: true,
  reason_for_change: true,
  candidate_status: true,
  added_by: true,
  uploaded_on: true,
  updated_on: true
});

  /* ---------- SEARCH ---------- */
  const [search, setSearch] = useState("");

  /* ---------- FILTER STATES ---------- */
  const [companyFilter, setCompanyFilter] = useState([]);
  const [designationFilter, setDesignationFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState("all"); // 'today', 'yesterday', 'last_week', 'last_month', 'custom'
  const [customDateRange, setCustomDateRange] = useState({ from: "", to: "" });

  const [companyList, setCompanyList] = useState([]);
  const [designationList, setDesignationList] = useState([]);

  const [openCompany, setOpenCompany] = useState(false);
  const [openDesignation, setOpenDesignation] = useState(false);
  const [openColumns, setOpenColumns] = useState(false);
  const [openDateFilter, setOpenDateFilter] = useState(false);


  const companyRef = useRef();
  const designationRef = useRef();
  const columnRef = useRef();
  const dateFilterRef = useRef();

  /* ---------- CLOSE DROPDOWNS ---------- */
  useEffect(() => {
    const handler = (e) => {
      if (companyRef.current && !companyRef.current.contains(e.target))
        setOpenCompany(false);
      if (designationRef.current && !designationRef.current.contains(e.target))
        setOpenDesignation(false);
      if (columnRef.current && !columnRef.current.contains(e.target))
        setOpenColumns(false);
      if (dateFilterRef.current && !dateFilterRef.current.contains(e.target))
        setOpenDateFilter(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ---------- FETCH DATA ---------- */
  const fetchReport = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("USER")) || {};
      const user_id = userData.id;

      const res = await getData("candidate-report/fetch/" + user_id);

      if (res?.status) {
      setRows(res.data || []);
    } else {
      console.error("Fetch failed:", res);
    }
  } catch (err) {
    console.error("Fetch error:", err);
  }
};

  useEffect(() => {
    fetchReport();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dateParam = params.get("date");
    if (dateParam) {
      setDateFilter(dateParam);
    }
  }, [location.search]);

  /* ---------- FILTER OPTIONS ---------- */
  useEffect(() => {
    setCompanyList([...new Set(rows.map((r) => r.company_applied).filter(Boolean))]);
    setDesignationList([...new Set(rows.map((r) => r.position).filter(Boolean))]);
  }, [rows]);

  /* ---------- DATE PARSER HELPER ---------- */
  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    const str = String(dateStr).trim();

    // DD-MM-YYYY or DD/MM/YYYY
    const dmyMatch = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dmyMatch) {
      const day = Number(dmyMatch[1]);
      const month = Number(dmyMatch[2]) - 1;
      const year = Number(dmyMatch[3]);
      const d = new Date(year, month, day);
      if (!isNaN(d.getTime())) {
        d.setHours(0, 0, 0, 0);
        return d;
      }
    }

    // YYYY-MM-DD or YYYY/MM/DD
    const ymdMatch = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (ymdMatch) {
      const year = Number(ymdMatch[1]);
      const month = Number(ymdMatch[2]) - 1;
      const day = Number(ymdMatch[3]);
      const d = new Date(year, month, day);
      if (!isNaN(d.getTime())) {
        d.setHours(0, 0, 0, 0);
        return d;
      }
    }

    // Fallback standard Date constructor
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      d.setHours(0, 0, 0, 0);
      return d;
    }

    return null;
  };

  /* ---------- DATE FILTER LOGIC ---------- */
  const applyDateFilter = (data) => {
    if (!dateFilter || dateFilter === "all") return data;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);

    const lastMonth = new Date(today);
    lastMonth.setMonth(today.getMonth() - 1);

    return data.filter((row) => {
      const uploadedDate = parseDateString(
        row.uploaded_on || row.application_date || row.updated_on
      );
      if (!uploadedDate) return false;

      switch (dateFilter) {
        case "today":
          return uploadedDate.getTime() === today.getTime();
        case "yesterday":
          return uploadedDate.getTime() === yesterday.getTime();
        case "last_week":
          return uploadedDate >= lastWeek && uploadedDate <= today;
        case "last_month":
          return uploadedDate >= lastMonth && uploadedDate <= today;
        case "custom":
          if (customDateRange.from && customDateRange.to) {
            const fromDate = parseDateString(customDateRange.from);
            const toDate = parseDateString(customDateRange.to);
            if (fromDate && toDate) {
              return uploadedDate >= fromDate && uploadedDate <= toDate;
            }
          }
          return true;
        default:
          return true;
      }
    });
  };

  /* ---------- FILTER + SEARCH ---------- */
  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();

    let processedRows = rows;

    // Apply company and designation filters first
    processedRows = processedRows.filter((r) => {
      const matchCompany =
        companyFilter.length === 0 ||
        companyFilter.includes(r.company_applied);

      const matchDesignation =
        designationFilter.length === 0 ||
        designationFilter.includes(r.position);

      return matchCompany && matchDesignation;
    });

    // Apply date filter
    processedRows = applyDateFilter(processedRows);

    // Apply search last
    return processedRows.filter((r) => {
      return Object.values(r).some((v) =>
        v?.toString().toLowerCase().includes(q)
      );
    });

  }, [rows, search, companyFilter, designationFilter, dateFilter, customDateRange]);

  /* ---------- EDIT ---------- */
  const openEdit = (row) => {
    setSelected({ ...row });
    setEditModal(true);
  };

const saveUpdate = async () => {

  const payload = {
    qualification: selected.qualification,
    current_company: selected.current_company,
    current_designation: selected.current_designation,
    current_ctc: selected.current_ctc,
    expected_ctc: selected.expected_ctc,
    notice_period: selected.notice_period,
    reason_for_change: selected.reason_for_change,
    candidate_status: selected.candidate_status,
  };

  const res = await putData(
    `candidate-report/update/${selected.candidate_id}`,
    payload
  );

  if (res?.status) {
    fetchReport();
    setEditModal(false);
  }
};

  /* ---------- DELETE ---------- */
const deleteCandidate = async (id) => {
  if (!window.confirm("Delete this candidate?")) return;

  try {
    const res = await deleteData(`candidate-report/delete/${id}`);

    if (res?.status) {
      fetchReport();
    } else {
      alert(res?.message || "Delete failed");
    }
  } catch (err) {
    console.error("Delete Error:", err);
  }
};

  /* ---------- EXPORT ---------- */
  const exportExcel = () => {
    const exportData = filteredRows.map((row) => {
      const obj = { "Candidate ID": row.candidate_id };
      Object.keys(columns).forEach((key) => {
        if (columns[key]) {
          obj[toCamelCase(key)] = row[key] || "";
        }
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, ws, "CandidateReport");

  XLSX.writeFile(wb, "CandidateReport.xlsx");
};

const getDateFilterLabel = () => {
    switch (dateFilter) {
      case "all": return "All Time";
      case "today": return "Today";
      case "yesterday": return "Yesterday";
      case "last_week": return "Last Week";
      case "last_month": return "Last Month";
      case "custom":
        if (customDateRange.from && customDateRange.to) {
          return `${customDateRange.from} to ${customDateRange.to}`;
        }
        return "Custom Range";
      default: return "Date Filter";
    }
  };

  return (
    <div className="w-full space-y-4">

      {/* HEADER */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-extrabold text-gray-800 uppercase tracking-wider">
            Candidate Reports
          </h2>
          <div className="mt-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
              Total: {filteredRows.length}
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={exportExcel}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-lg shadow-indigo-100 transition duration-200 active:scale-[0.99] flex items-center gap-2"
          >
            <FaDownload /> Export Excel 📄
          </button>

          {/* COLUMNS */}
          <div className="relative" ref={columnRef}>
            <button
              onClick={() => setOpenColumns((p) => !p)}
              className="px-4 py-2 bg-white border border-gray-200 text-xs font-bold text-gray-700 rounded-xl shadow-sm hover:bg-gray-50 transition duration-200 active:scale-[0.99] flex items-center gap-2"
            >
              <FaCog /> Columns
            </button>

            {openColumns && (
              <div className="absolute right-0 mt-2 bg-white p-4 border border-gray-100 rounded-2xl shadow-xl w-60 z-50 max-h-72 overflow-y-auto animate-fadeIn">
                {Object.keys(columns).map((c) => (
                  <label key={c} className="flex gap-2 text-xs py-1.5 cursor-pointer hover:bg-gray-50 items-center">
                    <input
                      type="checkbox"
                      checked={columns[c]}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      onChange={() =>
                        setColumns({ ...columns, [c]: !columns[c] })
                      }
                    />
                    {toCamelCase(c)}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FILTER PANEL */}
      <div className="border border-gray-100 rounded-2xl overflow-visible bg-white shadow-sm min-h-[480px]">
        <div className="border-b border-gray-100 bg-gray-50/20 p-4 space-y-3 relative z-30">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search candidate / company / email / phone..."
            className="h-10 w-full px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200"
          />

          <div className="flex flex-wrap gap-3 items-center">
            {/* COMPANY FILTER */}
            <div className="relative" ref={companyRef}>
              <button
                onClick={() => setOpenCompany((p) => !p)}
                className="h-10 px-4 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer flex justify-between items-center w-56 gap-2"
              >
                <span>Company ({companyFilter.length})</span>
                <FaChevronDown />
              </button>

              {openCompany && (
                <div className="absolute bg-white border border-gray-100 rounded-2xl mt-1.5 max-h-64 overflow-y-auto w-56 z-50 shadow-xl p-3 animate-fadeIn">
                  {companyList.map((c) => (
                    <label key={c} className="flex gap-2 text-xs py-1.5 cursor-pointer hover:bg-gray-50 items-center">
                      <input
                        type="checkbox"
                        checked={companyFilter.includes(c)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        onChange={() =>
                          setCompanyFilter((p) =>
                            p.includes(c)
                              ? p.filter((x) => x !== c)
                              : [...p, c]
                          )
                        }
                      />
                      {c}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* POSITION FILTER */}
            <div className="relative" ref={designationRef}>
              <button
                onClick={() => setOpenDesignation((p) => !p)}
                className="h-10 px-4 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer flex justify-between items-center w-56 gap-2"
              >
                <span>Position ({designationFilter.length})</span>
                <FaChevronDown />
              </button>

              {openDesignation && (
                <div className="absolute bg-white border border-gray-100 rounded-2xl mt-1.5 max-h-64 overflow-y-auto w-56 z-50 shadow-xl p-3 animate-fadeIn">
                  {designationList.map((d) => (
                    <label key={d} className="flex gap-2 text-xs py-1.5 cursor-pointer hover:bg-gray-50 items-center">
                      <input
                        type="checkbox"
                        checked={designationFilter.includes(d)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        onChange={() =>
                          setDesignationFilter((p) =>
                            p.includes(d)
                              ? p.filter((x) => x !== d)
                              : [...p, d]
                          )
                        }
                      />
                      {d}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* DATE FILTER */}
            <div className="relative" ref={dateFilterRef}>
              <button
                onClick={() => setOpenDateFilter((p) => !p)}
                className="h-10 px-4 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer flex justify-between items-center w-56 gap-2"
              >
                <span>{getDateFilterLabel()}</span>
                <FaChevronDown />
              </button>

              {openDateFilter && (
                <div className="absolute bg-white border border-gray-100 rounded-2xl mt-1.5 p-4 shadow-xl w-60 z-50 max-h-80 overflow-y-auto animate-fadeIn">
                  <div className="flex flex-col gap-2">
                    <label className="flex gap-2 text-xs py-1 items-center cursor-pointer">
                      <input
                        type="radio"
                        name="dateFilter"
                        checked={dateFilter === "all"}
                        className="text-indigo-600 focus:ring-indigo-500"
                        onChange={() => {
                          setDateFilter("all");
                          setCustomDateRange({ from: "", to: "" });
                        }}
                      />
                      All Time
                    </label>
                    <label className="flex gap-2 text-xs py-1 items-center cursor-pointer">
                      <input
                        type="radio"
                        name="dateFilter"
                        checked={dateFilter === "today"}
                        className="text-indigo-600 focus:ring-indigo-500"
                        onChange={() => {
                          setDateFilter("today");
                          setCustomDateRange({ from: "", to: "" });
                        }}
                      />
                      Today
                    </label>
                    <label className="flex gap-2 text-xs py-1 items-center cursor-pointer">
                      <input
                        type="radio"
                        name="dateFilter"
                        checked={dateFilter === "yesterday"}
                        className="text-indigo-600 focus:ring-indigo-500"
                        onChange={() => {
                          setDateFilter("yesterday");
                          setCustomDateRange({ from: "", to: "" });
                        }}
                      />
                      Yesterday
                    </label>
                    <label className="flex gap-2 text-xs py-1 items-center cursor-pointer">
                      <input
                        type="radio"
                        name="dateFilter"
                        checked={dateFilter === "last_week"}
                        className="text-indigo-600 focus:ring-indigo-500"
                        onChange={() => {
                          setDateFilter("last_week");
                          setCustomDateRange({ from: "", to: "" });
                        }}
                      />
                      Last Week
                    </label>
                    <label className="flex gap-2 text-xs py-1 items-center cursor-pointer">
                      <input
                        type="radio"
                        name="dateFilter"
                        checked={dateFilter === "last_month"}
                        className="text-indigo-600 focus:ring-indigo-500"
                        onChange={() => {
                          setDateFilter("last_month");
                          setCustomDateRange({ from: "", to: "" });
                        }}
                      />
                      Last Month
                    </label>
                    <label className="flex gap-2 text-xs py-1 items-center cursor-pointer">
                      <input
                        type="radio"
                        name="dateFilter"
                        checked={dateFilter === "custom"}
                        className="text-indigo-600 focus:ring-indigo-500"
                        onChange={() => setDateFilter("custom")}
                      />
                      Custom Range
                    </label>
                  </div>
                  {dateFilter === "custom" && (
                    <div className="flex flex-col gap-2 mt-3 animate-fadeIn">
                      <input
                        type="date"
                        className="h-9 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700"
                        value={customDateRange.from}
                        onChange={(e) =>
                          setCustomDateRange({ ...customDateRange, from: e.target.value })
                        }
                      />
                      <input
                        type="date"
                        className="h-9 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700"
                        value={customDateRange.to}
                        onChange={(e) =>
                          setCustomDateRange({ ...customDateRange, to: e.target.value })
                        }
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-150 bg-gray-50/50 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-left">
                <th className="px-4 py-3.5 w-16">Candidate ID</th>
                {Object.keys(columns).map(
                  (c) =>
                    columns[c] && (
                      <th key={c} className="px-4 py-3.5 whitespace-nowrap">
                        {toCamelCase(c)}
                      </th>
                    )
                )}
                <th className="px-4 py-3.5 w-24 text-center">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={Object.values(columns).filter(Boolean).length + 2} className="py-20 text-center">
                    <div className="flex flex-col items-center justify-center gap-2 max-w-sm mx-auto">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-50/80 flex items-center justify-center text-indigo-500 text-xl mb-1">
                        📄
                      </div>
                      <p className="text-sm font-bold text-gray-700">No Candidate Records Found</p>
                      <p className="text-xs text-gray-400 font-medium">Try adjusting your search keywords, company, or date filters.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRows.map((r, i) => (
                  <tr key={r.candidate_id} className="hover:bg-indigo-50/10 transition duration-150 text-gray-700">
                    <td className="px-4 py-3 font-semibold text-indigo-650">
                      #{r.candidate_id}
                    </td>

                    {Object.keys(columns).map(
                      (c) =>
                        columns[c] && (
                          <td key={c} className="px-4 py-3 whitespace-nowrap">
                            {c === "candidate_status" ? (
                              <span className="px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-750 text-[10px] font-bold uppercase tracking-wider">
                                {r[c]}
                              </span>
                            ) : (
                              r[c] || "-"
                            )}
                          </td>
                        )
                    )}

                    <td className="px-4 py-3 text-center flex items-center justify-center gap-1.5">
                      <button
                        onClick={() => openEdit(r)}
                        className="px-2 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg transition"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => deleteCandidate(r.candidate_id)}
                        className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-650 rounded-lg transition"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* EDIT MODAL */}
      <Modal open={editModal} title="Candidate Details" onClose={() => setEditModal(false)}>
        <div className="grid grid-cols-2 gap-4">
          {/* Candidate Name */}
          <div className="flex flex-col">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Candidate Name</label>
            <input
              className="h-10 px-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-450 outline-none"
              value={selected?.candidate_name || ""}
              readOnly
            />
          </div>

          {/* Email */}
          <div className="flex flex-col">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Email</label>
            <input
              className="h-10 px-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-450 outline-none"
              value={selected?.email_id || ""}
              readOnly
            />
          </div>

          {/* Phone */}
          <div className="flex flex-col">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Phone Number</label>
            <input
              className="h-10 px-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-450 outline-none"
              value={selected?.phone_number || ""}
              readOnly
            />
          </div>

          {/* Qualification */}
          <div className="flex flex-col">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Qualification</label>
            <input
              className="h-10 px-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-450 outline-none"
              value={selected?.qualification || ""}
              readOnly
            />
          </div>

          {/* Current Company */}
          <div className="flex flex-col">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Current Company</label>
            <input
              className="h-10 px-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-450 outline-none"
              value={selected?.current_company || ""}
              readOnly
            />
          </div>

          {/* Designation */}
          <div className="flex flex-col">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Current Designation</label>
            <input
              className="h-10 px-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-450 outline-none"
              value={selected?.current_designation || ""}
              readOnly
            />
          </div>

          {/* Current CTC */}
          <div className="flex flex-col">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Current CTC</label>
            <input
              className="h-10 px-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-450 outline-none"
              value={selected?.current_ctc || ""}
              readOnly
            />
          </div>

          {/* Expected CTC */}
          <div className="flex flex-col">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Expected CTC</label>
            <input
              className="h-10 px-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-450 outline-none"
              value={selected?.expected_ctc || ""}
              readOnly
            />
          </div>

          {/* Notice Period */}
          <div className="flex flex-col">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Notice Period</label>
            <input
              className="h-10 px-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-450 outline-none"
              value={selected?.notice_period || ""}
              readOnly
            />
          </div>

          {/* Reason For Change */}
          <div className="flex flex-col col-span-2">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Reason For Change</label>
            <textarea
              className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 h-20"
              placeholder="Enter reason for job change"
              value={selected?.reason_for_change || ""}
              onChange={(e) =>
                setSelected({ ...selected, reason_for_change: e.target.value })
              }
            />
          </div>

          {/* Candidate Status */}
          <div className="flex flex-col col-span-2">
            <label className="text-[10px] font-bold text-gray-450 uppercase tracking-wider block mb-1.5 ml-1">Candidate Status</label>
            <select
              className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
              value={selected?.candidate_status || ""}
              onChange={(e) =>
                setSelected({ ...selected, candidate_status: e.target.value })
              }
            >
              {[
                "Feedback Pending",
                "Screening",
                "Shortlisted",
                "Interview L1",
                "Interview L2",
                "Face to Face",
                "Selected",
                "Offered",
                "Joining Pending",
                "Joined",
                "Rejected",
                "No Show",
                "Hold",
                "Backout",
                "Duplicate",
              ].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* BUTTONS */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            className="px-4 py-2 border border-gray-200 text-xs font-bold text-gray-650 hover:bg-gray-50 rounded-xl transition duration-200 active:scale-[0.99]"
            onClick={() => setEditModal(false)}
          >
            Cancel
          </button>

          <button
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-lg shadow-indigo-150 transition duration-200 active:scale-[0.99]"
            onClick={saveUpdate}
          >
            Update Candidate
          </button>
        </div>
      </Modal>
    </div>
  );
}